import React, { useState, useRef } from 'react';
import {
    Box,
    Button,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Typography
} from '@mui/material';
import { UploadFileOutlinedIcon, FileDownloadOutlinedIcon, CheckCircleOutlineIcon } from 'icons/materialIcons';
import { CSVFileIcon } from 'icons/fileTypeIcons';
import TeamV2MemberRequiredFields from './TeamV2MemberRequiredFields';
import FileImportUtils from 'utils/contacts/FileImportUtils';

const TeamV2ImportCsvContent = ({ onDataChange, onValidationChange }) => {
    const [file, setFile] = useState(null);
    const [csvData, setCsvData] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [validationResults, setValidationResults] = useState({ valid: 0, invalid: 0 });
    const [currentStep, setCurrentStep] = useState(1); // 1 = Upload, 2 = Mapping
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!dragActive) setDragActive(true);
    };

    // Removed isValidImportFile - now using FileImportUtils.validateFileType() which handles all file types

    const processFile = async (file) => {
        if (!file) return;

        // Use FileImportUtils for validation (same as leads import)
        if (!FileImportUtils.validateFileType(file)) {
            setError(`File "${file.name}" is not a supported file type or exceeds size limit. Please upload CSV or Excel files.`);
            return;
        }

        // Call handleFileSelect directly with the file (same as leads import)
        await handleFileSelect(file);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const files = e.dataTransfer.files;
        if (files && files[0]) {
            await processFile(files[0]);
        }
    };

    const handleFileInput = async (e) => {
        const files = e.target.files;
        if (files && files[0]) {
            await processFile(files[0]);
        }
    };

    // Removed parseCsvFile - now using FileImportUtils.importFile() which handles both CSV and Excel files

    const handleFileSelect = async (selectedFile) => {
        if (!selectedFile) return;

        setFile(selectedFile);
        setError('');
        setLoading(true);

        try {
            // Use FileImportUtils.importFile() to handle both CSV and Excel files (same as leads import)
            const { data: importedData, columns } = await FileImportUtils.importFile(selectedFile);

            // Check if required headers exist (case-insensitive)
            const requiredHeaders = ['email'];
            const missingHeaders = requiredHeaders.filter(h =>
                !columns.some(header => header.toLowerCase() === h.toLowerCase())
            );

            if (missingHeaders.length > 0) {
                throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
            }

            // Filter out empty rows to ensure clean data
            const validRows = importedData.filter(row => {
                return Object.values(row).some(val =>
                    val !== null && val !== undefined && val !== '');
            });

            // Process data and validate emails
            const data = validRows.map((row, index) => {
                // Find email field (case-insensitive) for validation
                const emailField = columns.find(h => h.toLowerCase() === 'email');
                const emailValue = emailField ? (row[emailField] || '') : '';

                return {
                    ...row,
                    id: index,
                    isValid: validateEmail(emailValue)
                };
            });

            setCsvData(data);

            const valid = data.filter(row => row.isValid).length;
            const invalid = data.length - valid;
            setValidationResults({ valid, invalid });

            if (invalid > 0) {
                setError(`${invalid} rows have validation errors. Please review the data below.`);
            }

            // Notify parent
            const validData = data.filter(row => row.isValid);
            onDataChange(validData);
            onValidationChange(validData.length > 0);

            // Auto-advance to mapping step if data is successfully loaded
            if (data.length > 0) {
                setCurrentStep(2);
            }
        } catch (err) {
            setError(err.message);
            setCsvData([]);
            onDataChange([]);
            onValidationChange(false);
        } finally {
            setLoading(false);
        }
    };

    const downloadTemplate = () => {
        const csvContent = "email,name,jobtitle,company,location,bio,photo,phonenumber,website,linkedin,instagram\nexample@company.com,John Doe,Manager,Acme Inc,New York,Software developer with 5 years experience,profile.jpg,+1234567890,https://johndoe.com,https://linkedin.com/in/johndoe,@johndoe\n";
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'team_members_template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <Box sx={{ padding: '2rem', paddingTop: '3rem' }}>
            {/* Header with Title and Step Buttons */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem',
                width: '100%'
            }}>
                {/* Title */}
                <Typography variant="body2" sx={{
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: '#4f4f4f',
                    flex: '1'
                }}>
                    Import members
                </Typography>

                {/* Step Buttons */}
                <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                    <Box
                        onClick={() => setCurrentStep(1)}
                        sx={{
                            backgroundColor: currentStep === 1 ? '#FFFFFF' : '#F7F7F7',
                            color: '#4F4F4F',
                            border: currentStep === 1 ? '0.0625rem solid #E0E0E0' : 'none',
                            padding: '0 1.5rem',
                            height: '2.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: '6.25rem',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: currentStep === 1 ? '#FFFFFF' : '#EEEEEE'
                            }
                        }}
                    >
                        1-Upload
                    </Box>
                    <Box
                        onClick={() => csvData.length > 0 && setCurrentStep(2)}
                        sx={{
                            backgroundColor: currentStep === 2 ? '#FFFFFF' : '#F7F7F7',
                            color: csvData.length === 0 ? '#888888' : '#4F4F4F',
                            border: currentStep === 2 ? '0.0625rem solid #E0E0E0' : 'none',
                            padding: '0 1.5rem',
                            height: '2.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: '6.25rem',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            cursor: csvData.length > 0 ? 'pointer' : 'default',
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: csvData.length > 0 ? (currentStep === 2 ? '#FFFFFF' : '#EEEEEE') : '#F7F7F7'
                            }
                        }}
                    >
                        2-Mapping
                    </Box>
                </Box>
            </Box>

            {/* Content based on current step */}
            {currentStep === 1 && (
                <>
                    {/* File Upload Drag & Drop */}
                    <Box sx={{ marginBottom: '2.5rem' }}>
                        <Box
                            sx={{
                                width: '100%',
                                height: '18.125rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#ffffff',
                                borderRadius: '1.125rem',
                                border: dragActive ? '0.125rem dashed #29AEF8' : '0.125rem dashed #E0E0E0',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    borderColor: '#29AEF8',
                                    backgroundColor: 'rgba(41, 174, 248, 0.02)'
                                }
                            }}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            component="label"
                        >
                            <input
                                type="file"
                                accept=".csv,.xlsx,.xls,.ods,.txt,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.oasis.opendocument.spreadsheet,text/plain,application/octet-stream"
                                onChange={handleFileInput}
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                            />
                            <Box sx={{ textAlign: 'center' }}>
                                <Box
                                    sx={{
                                        width: '5rem',
                                        height: '5rem',
                                        backgroundColor: '#F7F7F7',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 1.5rem'
                                    }}
                                >
                                    <CSVFileIcon sx={{ fontSize: '2.5rem', color: '#4F4F4F' }} />
                                </Box>
                                <Typography variant="h6" sx={{ mb: '0.0625rem', color: '#000000', fontSize: '1.5rem', fontWeight: 600 }}>
                                    <span style={{ color: '#29AEF8' }}>Select CSV or Excel file</span> to upload
                                </Typography>
                                <Typography sx={{ color: '#4F4F4F', fontSize: '1rem', fontWeight: 500 }}>
                                    or drag and drop it here
                                </Typography>
                                <Typography sx={{ color: '#828282', fontSize: '0.8125rem', mt: '1.875rem' }}>
                                    Must be <b>.csv, .xlsx, .xls</b> file with maximum file size <b>50MB</b>
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* How to format the CSV */}
                    <Box sx={{ mb: '2.5rem' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '2.5rem' }}>
                            <Box>
                                <Typography variant="h6" sx={{ mb: '0.0625rem', fontSize: '1rem', fontWeight: 600, color: '#000000' }}>How to format the CSV</Typography>
                                <Typography sx={{ color: '#4F4F4F', fontSize: '0.8125rem' }}>
                                    Take a look at the possible columns below or download the CSV template.
                                </Typography>
                            </Box>
                            <Box
                                onClick={downloadTemplate}
                                sx={{
                                    backgroundColor: '#FFFFFF',
                                    color: '#4F4F4F',
                                    border: '0.0625rem solid #E0E0E0',
                                    padding: '0 1.5rem',
                                    height: '2.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    borderRadius: '6.25rem',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        backgroundColor: '#FFFFFF'
                                    }
                                }}
                            >
                                <FileDownloadOutlinedIcon sx={{ fontSize: '1rem' }} />
                                Download CSV Template
                            </Box>
                        </Box>
                        <TeamV2MemberRequiredFields />
                    </Box>

                    {/* Error Display */}
                    {error && (
                        <Alert
                            severity={validationResults.invalid > 0 ? "warning" : "error"}
                            sx={{ marginBottom: '1rem' }}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Validation Summary */}
                    {csvData.length > 0 && (
                        <Box>
                            <Box sx={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                                <Chip
                                    icon={<CheckCircleOutlineIcon />}
                                    label={`${validationResults.valid} Valid`}
                                    color="success"
                                    variant="outlined"
                                />
                                {validationResults.invalid > 0 && (
                                    <Chip
                                        label={`${validationResults.invalid} Invalid`}
                                        color="error"
                                        variant="outlined"
                                    />
                                )}
                            </Box>

                            {/* Data Preview */}
                            <TableContainer sx={{ maxHeight: '18.75rem', border: '0.0625rem solid #e0e0e0', borderRadius: '0.5rem' }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Job Title</TableCell>
                                            <TableCell>Company</TableCell>
                                            <TableCell>Location</TableCell>
                                            <TableCell>Phone</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {csvData.slice(0, 10).map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={row.isValid ? "Valid" : "Invalid"}
                                                        color={row.isValid ? "success" : "error"}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>{row.email}</TableCell>
                                                <TableCell>{row.name || '-'}</TableCell>
                                                <TableCell>{row.jobtitle || '-'}</TableCell>
                                                <TableCell>{row.company || '-'}</TableCell>
                                                <TableCell>{row.location || '-'}</TableCell>
                                                <TableCell>{row.phonenumber || '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {csvData.length > 10 && (
                                <Typography variant="caption" sx={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
                                    Showing first 10 rows of {csvData.length} total rows
                                </Typography>
                            )}
                        </Box>
                    )}
                </>
            )}

            {/* Mapping Step */}
            {currentStep === 2 && (
                <Box>
                    <Typography variant="body1" sx={{
                        marginBottom: '1.5rem',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#828282',
                        lineHeight: 1.5
                    }}>
                        Review and map your CSV columns to member fields. Ensure all required fields are mapped correctly.
                    </Typography>

                    {csvData.length > 0 ? (
                        <Box>
                            <Typography variant="body2" sx={{ marginBottom: '1rem', fontWeight: 600 }}>
                                Field Mapping Preview
                            </Typography>
                            <Alert severity="info" sx={{ marginBottom: '1.5rem' }}>
                                Mapping functionality will be implemented in the next phase. Currently showing data preview.
                            </Alert>

                            {/* Preview of uploaded data */}
                            <TableContainer component={Box} sx={{ border: '0.0625rem solid #e0e0e0', borderRadius: '0.5rem', maxHeight: '25rem', overflow: 'auto' }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            {csvData[0] && Object.keys(csvData[0]).filter(key => key !== 'isValid' && key !== 'errors').map((header) => (
                                                <TableCell key={header} sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                                                    {header}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {csvData.slice(0, 10).map((row, index) => (
                                            <TableRow key={index}>
                                                {Object.keys(row).filter(key => key !== 'isValid' && key !== 'errors').map((key) => (
                                                    <TableCell key={key}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                            {row[key]}
                                                            {!row.isValid && row.errors && row.errors.includes(key) && (
                                                                <Chip
                                                                    label="Invalid"
                                                                    size="small"
                                                                    color="error"
                                                                    sx={{ fontSize: '0.7rem' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : (
                        <Alert severity="warning">
                            No data uploaded yet. Please go to the Upload step and select a CSV file.
                        </Alert>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default TeamV2ImportCsvContent;
